import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Button,
	Divider,
	Checkbox,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Stack,
	Box,
} from "@mui/material";
import { useState, useMemo } from "react";
import { globalFormatCurrencyBRL } from "../../../../../shared/utils";
import { format } from "date-fns";

// Reutilizando a interface de Comission que você forneceu no componente pai
interface IComission {
	id: string;
	dueDate: string | null;
	status: string;
	type: string;
	value: string; // Valor da comissão
	description: string;
	cancelDate?: string | null; // Data de cancelamento, provável fonte do erro
}

interface ModalSelectComissionToReactivateProps {
	installments: IComission[]; // Agora recebe IComission[]
	onConfirm: (selectedIds: string[]) => void;
	onClose: () => void;
}

// Helper para formatar o tipo de comissão (mantido do modal pai)
const formatType = (type: string) => {
	const lowerType = type?.toLowerCase();
	if (lowerType === "prize") return "Premiação";
	if (lowerType === "bonus") return "Bônus";
	return type || "Comissão";
};

// Helper para formatar a data com segurança
const safeFormatDate = (dateString: string | null | undefined): string => {
	if (!dateString) return "Data Indisponível";

	try {
		const dateToFormat = new Date(dateString.includes("T") ? dateString : dateString + "T03:00:00");

		if (isNaN(dateToFormat.getTime())) {
			return "Data Inválida";
		}

		return format(dateToFormat, "dd/MM/yyyy");
	} catch {
		return "Erro de Formatação";
	}
};

export const ModalSelectComissionToReactivate = ({ installments, onConfirm, onClose }: ModalSelectComissionToReactivateProps) => {
	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	const canceledComissions = useMemo(
		() => installments.filter((item) => item.status.toLowerCase() === "canceled" || item.status.toLowerCase() === "cancelado"),
		[installments]
	);

	const sortedComissions = [...canceledComissions].sort((a, b) => {
		if (a.type < b.type) return -1;
		if (a.type > b.type) return 1;
		return 0;
	});

	const allSelected = selectedIds.length === sortedComissions.length;
	const partiallySelected = selectedIds.length > 0 && !allSelected;

	const toggleSelection = (id: string) => {
		setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
	};

	const toggleSelectAll = () => {
		if (allSelected) {
			setSelectedIds([]);
		} else {
			setSelectedIds(sortedComissions.map((item) => item.id));
		}
	};

	const handleConfirm = () => {
		if (selectedIds.length > 0) {
			onConfirm(selectedIds);
		}
	};

	return (
		<Dialog open onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
				<Typography variant="h6" fontWeight={400} color="primary.main">
					Selecione as **comissões** para reativar
				</Typography>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ px: 4, pt: 2, maxHeight: "60vh" }}>
				{sortedComissions.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Nenhuma comissão cancelada encontrada para reativação.
					</Typography>
				) : (
					<List dense disablePadding>
						{/* Checkbox de Selecionar Todos */}
						<ListItem divider>
							<ListItemIcon>
								<Checkbox edge="start" checked={allSelected} indeterminate={partiallySelected} onChange={toggleSelectAll} />
							</ListItemIcon>
							<ListItemText primary="Selecionar todas as comissões canceladas" primaryTypographyProps={{ fontWeight: 500 }} />
						</ListItem>

						{/* Lista de Comissões Canceladas */}
						{sortedComissions.map((item) => (
							<ListItem
								key={item.id}
								divider
								// Removemos o secondaryAction
							>
								<ListItemIcon>
									<Checkbox edge="start" checked={selectedIds.includes(item.id)} onChange={() => toggleSelection(item.id)} />
								</ListItemIcon>
								<ListItemText
									// NOVO ALINHAMENTO: Tipo e Valor na mesma linha (Primary)
									primary={
										<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
											<Typography variant="body1" fontWeight={500}>
												{formatType(item.type)}
											</Typography>
											<Typography variant="body1" fontWeight={700} color="primary.main">
												{globalFormatCurrencyBRL(item.value)}
											</Typography>
										</Box>
									}
									// Descrição e Data de Cancelamento no Secondary
									secondary={
										<Stack direction="row" spacing={1} alignItems="center">
											<Typography variant="caption" color="text.secondary">
												Descrição: {item.description || "N/A"}
											</Typography>
											<Typography variant="caption" color="text.disabled" sx={{ whiteSpace: "nowrap" }}>
												| Cancelada em: {safeFormatDate(item.cancelDate)}
											</Typography>
										</Stack>
									}
								/>
							</ListItem>
						))}
					</List>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
				<Stack direction="row" justifyContent="space-between" width="100%">
					<Button variant="outlined" onClick={onClose}>
						Fechar
					</Button>
					<Button variant="contained" color="primary" disabled={selectedIds.length === 0} onClick={handleConfirm}>
						Reativar Comissões ({selectedIds.length})
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};
